/**
 * TourTooltip — Custom joyride tooltip with crossfade + subtle rise on enter (#1574)
 *
 * Joyride remounts the tooltip between steps, so we can drive enter animation
 * via state without coordinating with joyride's lifecycle. The tooltip starts
 * at opacity 0 / translateY(4px) and animates to opacity 1 / translateY(0)
 * over 180ms ease-out. Honors prefers-reduced-motion by dropping the rise.
 *
 * Visual styling matches the previous joyride-styles config (Layer 2 tokens
 * via `var(--oro-*)` so they resolve inside JS-returned style objects, per
 * #1568 lesson).
 */

import { useEffect, useState } from 'react';
import type { TooltipRenderProps } from 'react-joyride';

const ENTER_DURATION_MS = 180;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function TourTooltip({
  backProps,
  continuous,
  index,
  isLastStep,
  primaryProps,
  size,
  skipProps,
  step,
  tooltipProps,
}: TooltipRenderProps) {
  const [entered, setEntered] = useState(false);
  const [prevIndex, setPrevIndex] = useState(index);
  const reducedMotion = prefersReducedMotion();

  // React docs "adjust state on prop change" — reset enter animation when step changes.
  if (prevIndex !== index) {
    setPrevIndex(index);
    setEntered(false);
  }

  useEffect(() => {
    // Two rAFs ensure the initial style commits before transitioning to entered state
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setEntered(true));
    });
    return () => cancelAnimationFrame(id);
  }, [index]);

  const transform = reducedMotion
    ? 'none'
    : entered
      ? 'translateY(0)'
      : 'translateY(4px)';

  return (
    <div
      {...tooltipProps}
      style={{
        backgroundColor: 'var(--oro-surface)',
        border: '1px solid var(--oro-border)',
        borderRadius: 12,
        padding: 16,
        maxWidth: 320,
        color: 'var(--text-primary)',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.4)',
        opacity: entered ? 1 : 0,
        transform,
        transition: reducedMotion
          ? `opacity ${ENTER_DURATION_MS}ms ease-out`
          : `opacity ${ENTER_DURATION_MS}ms ease-out, transform ${ENTER_DURATION_MS}ms ease-out`,
      }}
    >
      {step.title && (
        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
            marginBottom: 4,
            color: 'var(--text-primary)',
          }}
        >
          {step.title}
        </div>
      )}
      <div
        style={{
          fontSize: 14,
          lineHeight: 1.4,
          color: 'var(--text-secondary)',
          marginBottom: 16,
        }}
      >
        {step.content}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <button
          {...skipProps}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-secondary)',
            fontSize: 14,
            minHeight: 44,
            padding: '0 8px',
            cursor: 'pointer',
          }}
        >
          Skip
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {index > 0 && (
            <button
              {...backProps}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-primary)',
                fontSize: 14,
                minHeight: 44,
                padding: '0 12px',
                cursor: 'pointer',
              }}
            >
              Back
            </button>
          )}
          <button
            {...primaryProps}
            style={{
              backgroundColor: 'var(--oro-gold)',
              color: '#000',
              fontSize: 14,
              fontWeight: 600,
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              minHeight: 44,
              cursor: 'pointer',
            }}
          >
            {isLastStep ? 'Done' : continuous ? `Next (${index + 1}/${size})` : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
}
